import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Activity, CheckCircle2, XCircle, Loader2, PlayCircle, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const Dot = ({ ok }) => ok
  ? <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" />
  : <XCircle className="w-4 h-4 text-red-400 inline" />;

export default function RenderDiagnostics({ defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/render/diagnostics"); setDiag(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (defaultOpen) load(); }, [defaultOpen]);
  const toggle = () => { const nx = !open; setOpen(nx); if (nx && !diag) load(); };
  const runTest = async () => {
    setTesting(true); setTest(null);
    try {
      const { data } = await api.post("/render/selftest");
      setTest(data);
      data.ok ? toast.success("Test render passed all checks") : toast.error("Test render found a problem");
    } catch (e) { toast.error(apiError(e)); }
    finally { setTesting(false); }
  };

  const Row = ({ label, value }) => (
    <div className="flex justify-between py-1 border-b border-navy-border/50 text-sm">
      <span className="text-muted-foreground">{label}</span><span className="text-ivory">{value}</span>
    </div>
  );

  return (
    <div className="rounded-2xl border border-navy-border bg-navy-card overflow-hidden">
      <button data-testid="diagnostics-toggle" onClick={toggle} className="w-full flex items-center justify-between px-5 py-4">
        <span className="flex items-center gap-2 text-champagne text-sm uppercase tracking-wider"><Activity className="w-4 h-4 text-gold" /> Render Diagnostics</span>
        <ChevronDown className={`w-4 h-4 text-champagne transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4" data-testid="diagnostics-panel">
          <div className="flex gap-2">
            <Button data-testid="diagnostics-refresh" size="sm" variant="outline" onClick={load} disabled={loading} className="border-navy-border text-champagne">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
            </Button>
            <Button data-testid="run-test-render" size="sm" onClick={runTest} disabled={testing} className="rounded-full bg-gold hover:bg-gold-dark text-navy font-semibold">
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <PlayCircle className="w-4 h-4 mr-1.5" />} Run small test render
            </Button>
          </div>
          {diag && (
            <div className="grid sm:grid-cols-2 gap-x-6">
              <div>
                <Row label="FFmpeg available" value={<Dot ok={diag.ffmpeg_available} />} />
                <Row label="FFprobe available" value={<Dot ok={diag.ffprobe_available} />} />
                <Row label="CPU architecture" value={diag.cpu_arch} />
                <Row label="Render worker" value={<Dot ok={diag.worker_online} />} />
                <Row label="Private storage" value={<Dot ok={diag.storage_connected} />} />
              </div>
              <div>
                <Row label="Active jobs" value={diag.active_jobs} />
                <Row label="Queued jobs" value={diag.queued_jobs} />
                <Row label="Stuck jobs" value={<span className={diag.stuck_jobs ? "text-orange-400" : "text-ivory"}>{diag.stuck_jobs}</span>} />
                <Row label="Temp disk free" value={`${(diag.temp_free_mb / 1024).toFixed(1)} GB`} />
                <Row label="Recent failures" value={diag.recent_failures?.length || 0} />
              </div>
            </div>
          )}
          {test && (
            <div className="rounded-xl border border-navy-border p-3" data-testid="test-render-result">
              <p className="text-sm text-ivory mb-2">Test render: {test.ok ? <span className="text-emerald-400">PASSED</span> : <span className="text-red-400">FAILED</span>}</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs">
                {Object.entries(test.steps || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k.replace(/_/g, " ")}</span><Dot ok={v} /></div>
                ))}
              </div>
              {test.error && <p className="text-xs text-red-400 mt-2">{test.error}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
